import { DropDownTreeChangeEvent, ItemProps } from '@progress/kendo-react-dropdowns';
import { isEmpty } from 'lodash';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TreeDropDown from 'src/components/design-system/DropDownTree';
import { processTreeData } from 'src/utils/treeDataOperations';

interface Item {
  content: string;
  id: number;
  expanded?: boolean;
  selected?: boolean;
}

export interface Category {
  content: string;
  id: number;
  items: Item[];
  parent?: boolean;
}

const selectField = 'selected';
const expandField = 'expanded';
const dataItemKey = 'id';
const textField = 'content';
const subItemsField = 'items';
const fields = { selectField, expandField, dataItemKey, subItemsField };

interface ExcelProps {
  handleExcelChange: (id: number) => void;
  options: Category[];
}
export const initialExpandedState = [1, 4];
const NestedExcelDropdown: FC<ExcelProps> = ({ handleExcelChange, options }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<number[]>(initialExpandedState);
  const [dropDownOpened, setDropDownOpened] = useState<boolean>(false);
  const [dropDownValue, setDropDownValue] = useState<Category | null>(null);

  const treeData = useMemo(
    () => processTreeData(options, { expanded, dropDownValue }, fields),
    [dropDownValue, expanded, options]
  );

  const onChange = useCallback(
    (event: DropDownTreeChangeEvent) => {
      const { level = [], value = {} } = event;
      const { id = 0 } = value || {};

      if (isEmpty(level) || level.length > 1) {
        setDropDownOpened(false);
        setDropDownValue(value);
        handleExcelChange(id);
        setTimeout(() => setDropDownValue(null), 100);
        setExpanded(initialExpandedState);
      } else if (level.length === 1) {
        setExpanded((prev) => {
          if (prev.includes(id)) {
            return prev.filter((item) => item !== id);
          } else {
            return expanded.length ? initialExpandedState : [id];
          }
        });
      }
    },
    [expanded, handleExcelChange]
  );

  const Item = useCallback(
    (props: ItemProps) => {
      const { selected = false, content, parent = false } = props.item;
      return (
        <>
          {parent ? (
            <span
              onClick={() => setDropDownOpened(true)}
              className={`cursor-default hover:cursor-pointer`}
            >
              {content}
            </span>
          ) : (
            <span className={`cursor-default hover:cursor-pointer`}>
              <span
                style={
                  selected
                    ? {
                        color: '#94C947',
                        marginLeft: '-16px',
                        marginRight: '2px',
                        fontSize: '14px',
                      }
                    : {}
                }
                className={`${selected ? 'k-icon k-i-check-outline k-i-checkmark-outline' : ''}`}
              />
              {content}
            </span>
          )}
        </>
      );
    },
    [setDropDownOpened]
  );

  return (
    <TreeDropDown
      expanded={expanded}
      setExpanded={setExpanded}
      dropDownOpened={dropDownOpened}
      setDropDownOpened={setDropDownOpened}
      id="view-drop-down-list"
      style={{ width: '200px' }}
      data={treeData}
      value={dropDownValue}
      placeholder={t('Export to Excel...')}
      textField={textField}
      dataItemKey={dataItemKey}
      selectField={selectField}
      expandField={expandField}
      onChange={onChange}
      item={Item}
      onBlurCloseAll={true}
    />
  );
};

export default NestedExcelDropdown;
